import moment from "moment";
import Close from "@mui/icons-material/Close";
import { DateRangePicker } from "react-date-range";
import { memo, useEffect, useMemo, useState } from "react";
import { Box, FormField, Button } from "@maysoft/common-component-react";
import Chip from "@mui/material/Chip";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import IconButton from "@mui/material/IconButton";
import styled from "@mui/material/styles/styled";

import Helpers from "commons/helpers";
import Strings from "constants/strings";

import { Mode } from "constants/enum";
import { getDefaultRanges, isSameRange } from "./RangeDefault";

import "react-date-range/dist/styles.css"; // main style file
import "react-date-range/dist/theme/default.css"; // theme css file

const BootstrapDialog = styled(Dialog)(({ theme }) => ({
    "& .MuiDialogContent-root": {
        padding: theme.spacing(1),
    },
    "& .MuiDialogActions-root": {
        padding: theme.spacing(1),
    },
}));

interface Props {
    mode?: Mode;
    width?: any;
    label?: string;
    format?: string;
    disabled?: boolean;
    required?: boolean;
    readOnly?: boolean;
    placeholder?: string;
    errorMessage?: string;
    allowChipSuggest?: boolean;
    variant?: "standard" | "outlined";
    value: {
        endDate?: number;
        startDate?: number;
    };
    onChangeValue: (value: { startDate?: number; endDate?: number }) => void;
};

const PopupDateRangePicker = (props: Props): JSX.Element => {

    const [state, setState] = useState<any[]>([]);
    const [isCustom, setIsCustom] = useState<boolean>(false);
    const [calenderPopupVisibled, setCalenderPopupVisibled] = useState<boolean>(false);

    const dataRange = useMemo(() => getDefaultRanges(moment().endOf("day").toDate()), []);

    const formatValue = useMemo(() => {
        const formatString = props.format || "dd/mm/yyyy";
        if (
            !Helpers.isNullOrEmpty(props.value?.endDate) &&
            !Helpers.isNullOrEmpty(props.value?.startDate) &&
            (props.value?.endDate !== 0) && (props.value?.startDate !== 0)
        ) {
            const strEnd = Helpers.dateToString(props.value?.endDate);
            const strStart = Helpers.dateToString(props.value?.startDate);
            return `${Strings.COMMON.FROM}: ${strStart} - ${Strings.COMMON.TO}: ${strEnd}`;
        } else {
            return `${Strings.COMMON.FROM}: ${formatString} - ${Strings.COMMON.TO}: ${formatString}`;
        }
    }, [props.format, props.value?.startDate, props.value?.endDate]);

    useEffect(() => {
        if (
            !Helpers.isNullOrEmpty(props.value?.endDate) &&
            !Helpers.isNullOrEmpty(props.value?.startDate) &&
            (props.value?.endDate !== 0) && (props.value?.startDate !== 0)
        ) {
            setState([
                {
                    key: "selection",
                    endDate: moment(props.value?.endDate).toDate(),
                    startDate: moment(props.value?.startDate).toDate(),
                },
            ]);
        } else {
            setState([
                {
                    key: "selection",
                    endDate: moment().toDate(),
                    startDate: moment().toDate(),
                },
            ]);
        }
    }, [calenderPopupVisibled]);

    return (
        <>
            <FormField
                mode={props.mode}
                label={props.label}
                value={formatValue}
                variant={props.variant}
                contentEditable={false}
                disabled={props.disabled}
                required={props.required}
                readOnly={props.readOnly}
                placeholder={props.placeholder}
                errorMessage={props.errorMessage}
                onClick={() => {
                    (!props.disabled && !props.readOnly && props.mode !== Mode.View)
                        && setCalenderPopupVisibled(true);
                }}
                InputProps={{
                    startAdornment: <CalendarIcon />,
                    endAdornment:
                        (!props.disabled && !props.readOnly && props.mode !== Mode.View) &&
                        (
                            <IconButton
                                size={"small"}
                                sx={{
                                    p: 0,
                                    ".MuiSvgIcon-root": {
                                        width: "20px",
                                        height: "20px",
                                        fontSize: "1.25rem",
                                    }
                                }}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    props.onChangeValue({
                                        endDate: undefined,
                                        startDate: undefined,
                                    });
                                }}
                            >
                                <Close />
                            </IconButton>
                        ),
                }}
            />

            {calenderPopupVisibled &&
                <BootstrapDialog
                    fullWidth
                    maxWidth={"md"}
                    sx={{
                        "& .MuiDialog-container": {
                            "& .MuiPaper-root": {
                                width: "100%",
                                maxWidth: "708px",  // Set your width here
                            },
                        },
                    }}
                    open={calenderPopupVisibled}
                    aria-labelledby={"customized-dialog-title"}
                    onClose={() => { setCalenderPopupVisibled(false); }}
                >
                    <DialogTitle sx={{ m: 0 }}>
                        <Box sx={{
                            display: "flex",
                            alignItems: "center",
                        }}>
                            {props.label}
                        </Box>
                    </DialogTitle>
                    <DialogContent sx={{ m: 0, p: 0, }}>
                        <Box
                            sx={{
                                p: 2,
                                display: "contents",
                                "& .rdrCalendarWrapper": {
                                    fontSize: "12.5px",
                                },
                                "& .rdrDefinedRangesWrapper, .rdrDateDisplay": {
                                    display: "none",
                                },
                                "& .rdrDayToday .rdrDayNumber span:after": {
                                    bottom: "1px",
                                },
                                "& .rdrDayDisabled": {
                                    backgroundColor: "transparent"
                                }
                            }}
                        >
                            <DateRangePicker
                                months={2}
                                ranges={state}
                                direction={"horizontal"}
                                moveRangeOnFirstSelection={false}
                                onChange={(item: any) => {
                                    setState([
                                        {
                                            key: "selection",
                                            endDate: item?.selection?.endDate,
                                            startDate: item?.selection?.startDate,
                                        }
                                    ]);
                                }}
                            />
                            {props.allowChipSuggest &&
                                <Box sx={{
                                    p: 2,
                                    pt: 1,
                                    gap: 1,
                                    display: "flex",
                                    flexWrap: "wrap",
                                    alignItems: "center",
                                    borderTop: "1px solid #cdcdcd",
                                }}>
                                    {
                                        dataRange.map((item) => {
                                            const isTrue = !isCustom && isSameRange(
                                                {
                                                    endDate: state?.[0]?.endDate,
                                                    startDate: state?.[0]?.startDate,
                                                },
                                                { startDate: item.startDate, endDate: item.endDate }
                                            );
                                            return (
                                                <Chip
                                                    key={item.label}
                                                    label={item.label}
                                                    color={isTrue ? "info" : "secondary"}
                                                    variant={isTrue ? "filled" : "outlined"}
                                                    sx={{ height: "26px", minWidth: "120px" }}
                                                    onClick={() => {
                                                        setIsCustom(false);
                                                        setState([
                                                            {
                                                                key: "selection",
                                                                endDate: item.endDate,
                                                                startDate: item.startDate,
                                                            }
                                                        ]);
                                                    }}
                                                />
                                            )
                                        })
                                    }
                                    <Chip
                                        key={"item_custom"}
                                        label={Strings.COMMON.CUSTOM}
                                        color={isCustom ? "info" : "secondary"}
                                        variant={isCustom ? "filled" : "outlined"}
                                        sx={{ marginTop: "4px", marginRight: "4px", height: "26px", width: "120px" }}
                                        onClick={() => {
                                            !isCustom && setIsCustom(true);
                                            if (state?.[0]?.endDate === undefined && state?.[0]?.startDate === undefined) {
                                                setState([
                                                    {
                                                        key: "selection",
                                                        endDate: moment().toDate(),
                                                        startDate: moment().toDate(),
                                                    }
                                                ]);
                                            }
                                        }}
                                    />
                                </Box>
                            }
                        </Box>
                    </DialogContent>
                    <DialogActions>
                        <Button color={"secondary"} onClick={() => { setCalenderPopupVisibled(false); }}>
                            {Strings.COMMON.CANCEL}
                        </Button>
                        <Button color={"info"} onClick={() => {
                            setCalenderPopupVisibled(false);
                            props.onChangeValue({
                                endDate: moment(state?.[0]?.endDate).endOf("day").unix(),
                                startDate: moment(state?.[0]?.startDate).endOf("day").unix(),
                            });
                        }}>
                            {Strings.COMMON.SELECTE}
                        </Button>
                    </DialogActions>
                </BootstrapDialog>
            }
        </>
    );
};

export default memo(PopupDateRangePicker);

export const CalendarIcon = () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path
            d="M12 14C11.7167 14 11.4793 13.904 11.288 13.712C11.0967 13.52 11.0007 13.2827 11 13C10.9993 12.7173 11.0953 12.48 11.288 12.288C11.4807 12.096 11.718 12 12 12C12.282 12 12.5197 12.096 12.713 12.288C12.9063 12.48 13.002 12.7173 13 13C12.998 13.2827 12.902 13.5203 12.712 13.713C12.522 13.9057 12.2847 14.0013 12 14ZM8 14C7.71667 14 7.47933 13.904 7.288 13.712C7.09667 13.52 7.00067 13.2827 7 13C6.99933 12.7173 7.09533 12.48 7.288 12.288C7.48067 12.096 7.718 12 8 12C8.282 12 8.51967 12.096 8.713 12.288C8.90633 12.48 9.002 12.7173 9 13C8.998 13.2827 8.902 13.5203 8.712 13.713C8.522 13.9057 8.28467 14.0013 8 14ZM16 14C15.7167 14 15.4793 13.904 15.288 13.712C15.0967 13.52 15.0007 13.2827 15 13C14.9993 12.7173 15.0953 12.48 15.288 12.288C15.4807 12.096 15.718 12 16 12C16.282 12 16.5197 12.096 16.713 12.288C16.9063 12.48 17.002 12.7173 17 13C16.998 13.2827 16.902 13.5203 16.712 13.713C16.522 13.9057 16.2847 14.0013 16 14ZM12 18C11.7167 18 11.4793 17.904 11.288 17.712C11.0967 17.52 11.0007 17.2827 11 17C10.9993 16.7173 11.0953 16.48 11.288 16.288C11.4807 16.096 11.718 16 12 16C12.282 16 12.5197 16.096 12.713 16.288C12.9063 16.48 13.002 16.7173 13 17C12.998 17.2827 12.902 17.5203 12.712 17.713C12.522 17.9057 12.2847 18.0013 12 18ZM8 18C7.71667 18 7.47933 17.904 7.288 17.712C7.09667 17.52 7.00067 17.2827 7 17C6.99933 16.7173 7.09533 16.48 7.288 16.288C7.48067 16.096 7.718 16 8 16C8.282 16 8.51967 16.096 8.713 16.288C8.90633 16.48 9.002 16.7173 9 17C8.998 17.2827 8.902 17.5203 8.712 17.713C8.522 17.9057 8.28467 18.0013 8 18ZM16 18C15.7167 18 15.4793 17.904 15.288 17.712C15.0967 17.52 15.0007 17.2827 15 17C14.9993 16.7173 15.0953 16.48 15.288 16.288C15.4807 16.096 15.718 16 16 16C16.282 16 16.5197 16.096 16.713 16.288C16.9063 16.48 17.002 16.7173 17 17C16.998 17.2827 16.902 17.5203 16.712 17.713C16.522 17.9057 16.2847 18.0013 16 18ZM5 22C4.45 22 3.97933 21.8043 3.588 21.413C3.19667 21.0217 3.00067 20.5507 3 20V6C3 5.45 3.196 4.97933 3.588 4.588C3.98 4.19667 4.45067 4.00067 5 4H6V2H8V4H16V2H18V4H19C19.55 4 20.021 4.196 20.413 4.588C20.805 4.98 21.0007 5.45067 21 6V20C21 20.55 20.8043 21.021 20.413 21.413C20.0217 21.805 19.5507 22.0007 19 22H5ZM5 20H19V10H5V20Z"
            fill="#637381"
        />
    </svg>
);
